import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import { userDetailContext } from '../../../App';
import PartnerMainContainer from '../../../components/PartnerMainContainer';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import { hasPrivilege } from '../../../utils/privilege';
import ResiliationValidationService from '../../../services/ResiliationValidationService';

const STATUS_LABELS = {
  PENDING: 'En attente',
  APPROVED: 'Validée',
  REJECTED: 'Rejetée',
};

const formatDate = (value, withTime = false) => {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('fr-FR', withTime
    ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : undefined);
};

const formatAmount = (value) => `${Number(value || 0).toLocaleString('fr-FR')} Ar`;

const DetailLine = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2">{value || '—'}</Typography>
  </Box>
);

const ResiliationValidation = () => {
  const contextData = useContext(userDetailContext);
  const canValidate = hasPrivilege(contextData?.privileges || [], 'valider_resiliation_partenaire');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('PENDING');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState('');

  const loadRows = useCallback(async () => {
    if (!canValidate) return;
    setLoading(true);
    const result = await ResiliationValidationService.getAll({
      status,
      search,
      page: page + 1,
      limit: rowsPerPage,
    });
    setLoading(false);
    if (!result?.status) {
      setRows([]);
      setTotal(0);
      setMessage({ severity: 'error', text: result?.message || 'Chargement impossible' });
      return;
    }
    setRows(Array.isArray(result.data?.rows) ? result.data.rows : []);
    setTotal(Number(result.data?.total) || 0);
  }, [canValidate, page, rowsPerPage, search, status]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const showDetail = async (row) => {
    setLoading(true);
    const result = await ResiliationValidationService.getDetail(row.validation_resiliation_id);
    setLoading(false);
    if (!result?.status) {
      setMessage({ severity: 'error', text: result?.message || 'Détail indisponible' });
      return;
    }
    setDetail(result.data);
    setDetailOpen(true);
  };

  const submitDecision = async () => {
    if (!decision) return;
    if (decision.type === 'reject' && !reason.trim()) {
      setMessage({ severity: 'warning', text: 'Le motif du rejet est obligatoire.' });
      return;
    }

    setLoading(true);
    const result = decision.type === 'approve'
      ? await ResiliationValidationService.approve(decision.row.validation_resiliation_id)
      : await ResiliationValidationService.reject(decision.row.validation_resiliation_id, reason.trim());
    setLoading(false);

    if (!result?.status) {
      setMessage({ severity: 'error', text: result?.message || 'Décision impossible' });
      return;
    }

    setDecision(null);
    setReason('');
    setMessage({ severity: 'success', text: result.message });
    loadRows();
  };

  if (!canValidate) {
    return (
      <PartnerMainContainer>
        <Alert severity="error">Permission valider_resiliation_partenaire requise.</Alert>
      </PartnerMainContainer>
    );
  }

  return (
    <PartnerMainContainer>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={loading} />
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Validation des contrats résiliés</Typography>
          <Typography variant="body2" color="text.secondary">
            Examinez les résiliations proposées avant leur application aux contrats partenaires.
          </Typography>
        </Box>

        {message && (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>
        )}

        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              size="small"
              label="Statut"
              value={status}
              onChange={(event) => { setStatus(event.target.value); setPage(0); }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="PENDING">En attente</MenuItem>
              <MenuItem value="APPROVED">Validées</MenuItem>
              <MenuItem value="REJECTED">Rejetées</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Contrat, partenaire, objet ou fichier"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') { setSearch(searchInput.trim()); setPage(0); }
              }}
              fullWidth
            />
            <Button variant="contained" onClick={() => { setSearch(searchInput.trim()); setPage(0); }}>
              Rechercher
            </Button>
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contrat</TableCell>
                <TableCell>Partenaire</TableCell>
                <TableCell>Situation actuelle</TableCell>
                <TableCell>Date proposée</TableCell>
                <TableCell>Cause proposée</TableCell>
                <TableCell>Fichier source</TableCell>
                <TableCell>Date demande</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center">Aucune demande trouvée.</TableCell></TableRow>
              ) : rows.map((row) => (
                <TableRow key={row.validation_resiliation_id} hover>
                  <TableCell>{row.ref_num_contrat || '—'}</TableCell>
                  <TableCell>{row.partenaire || '—'}</TableCell>
                  <TableCell><Chip size="small" label={row.situation_avant || 'Non renseignée'} /></TableCell>
                  <TableCell>{formatDate(row.date_rupture_proposee)}</TableCell>
                  <TableCell>{row.cause_rupture_proposee || '—'}</TableCell>
                  <TableCell>{row.source_file || '—'}</TableCell>
                  <TableCell>{formatDate(row.requested_at, true)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => showDetail(row)}>Voir</Button>
                      {row.statut === 'PENDING' && (
                        <>
                          <Button color="success" size="small" startIcon={<CheckCircleOutlineIcon />} onClick={() => setDecision({ type: 'approve', row })}>Valider</Button>
                          <Button color="error" size="small" startIcon={<CancelOutlinedIcon />} onClick={() => setDecision({ type: 'reject', row })}>Rejeter</Button>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="Lignes par page"
          />
        </TableContainer>
      </Stack>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Détail de la demande de résiliation</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                <DetailLine label="Contrat" value={detail.ref_num_contrat} />
                <DetailLine label="Partenaire" value={detail.partenaire} />
                <DetailLine label="Statut" value={STATUS_LABELS[detail.statut]} />
              </Stack>
              <DetailLine label="Objet" value={detail.objet} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                <DetailLine label="Direction" value={detail.direction} />
                <DetailLine label="Financement" value={detail.financement} />
                <DetailLine label="Composante" value={detail.composante} />
                <DetailLine label="Zone" value={detail.zone} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                <DetailLine label="Montant" value={formatAmount(detail.montant)} />
                <DetailLine label="Début du contrat" value={formatDate(detail.date_debut)} />
                <DetailLine label="Fin du contrat" value={formatDate(detail.date_fin)} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                <DetailLine label="Situation actuelle" value={detail.situation_avant} />
                <DetailLine label="Situation proposée" value={detail.situation_proposee} />
                <DetailLine label="Date de rupture proposée" value={formatDate(detail.date_rupture_proposee)} />
              </Stack>
              <DetailLine label="Cause proposée" value={detail.cause_rupture_proposee} />
              <DetailLine label="Fichier source" value={detail.source_file} />
              <DetailLine label="Demandée par" value={detail.requester_name || `Utilisateur #${detail.requested_by || '—'}`} />
              {detail.statut !== 'PENDING' && (
                <>
                  <DetailLine label="Décidée par" value={detail.reviewer_name || `Utilisateur #${detail.reviewed_by || '—'}`} />
                  <DetailLine label="Date de décision" value={formatDate(detail.reviewed_at, true)} />
                  {detail.statut === 'REJECTED' && <DetailLine label="Motif du rejet" value={detail.rejection_reason} />}
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailOpen(false)}>Fermer</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(decision)} onClose={() => { setDecision(null); setReason(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>{decision?.type === 'approve' ? 'Valider la résiliation' : 'Rejeter la résiliation'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Contrat {decision?.row.ref_num_contrat || '—'} — {decision?.row.partenaire || '—'}
          </Typography>
          {decision?.type === 'approve' ? (
            <Alert severity="warning">Cette action marquera immédiatement le contrat comme résilié.</Alert>
          ) : (
            <TextField
              autoFocus
              multiline
              minRows={3}
              fullWidth
              required
              inputProps={{ maxLength: 500 }}
              label="Motif du rejet"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDecision(null); setReason(''); }}>Annuler</Button>
          <Button
            variant="contained"
            color={decision?.type === 'approve' ? 'success' : 'error'}
            disabled={decision?.type === 'reject' && !reason.trim()}
            onClick={submitDecision}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </PartnerMainContainer>
  );
};

export default ResiliationValidation;
